import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { forwardRef, useRef, useState, useEffect, createRef, useMemo, Children } from 'react';
import { Fade, Slide } from '@wds/motion';
import { safeRest, useIntersectionObserver, KEYS, defaultFonts, useIsomorphicLayoutEffect, isBrowser, usePreviousState, uniqueId, isFocusableElement } from '@wds/utils';
import { createMemoStyles, SIZES } from '@wds/styles';
import { Overlay } from '@wds/internal-components';
import { IconX } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles$5 = createMemoStyles(function (theme) {
  var elevation = theme.elevation,
    spacing = theme.spacing,
    type = theme.type,
    radius = theme.radius,
    palette = theme.palette;
  return {
    wrenchModalCloseButton: {
      fontSize: type.fontSize.body,
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: function (_a) {
        var disabled = _a.disabled;
        return !disabled && 'pointer';
      },
      borderRadius: radius.small,
      textDecoration: 'none',
      background: function (_a) {
        var onDark = _a.onDark;
        return onDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)';
      },
      border: 'none',
      position: 'absolute',
      top: spacing[5],
      right: spacing[5],
      padding: spacing[2],
      zIndex: elevation.sky,
      '&:hover': {
        background: function (_a) {
          var onDark = _a.onDark;
          return onDark ? palette.background.hover : palette.background.hoverOnDark;
        },
        transition: 'none',
        textDecoration: 'none'
      },
      '&:active': {
        background: function (_a) {
          var onDark = _a.onDark;
          return onDark ? palette.background.pressed : palette.background.pressedOnDark;
        },
        transition: 'none',
        outline: 'none'
      }
    }
  };
});

var CloseButton = function CloseButton(_a) {
  _a.children;
    var label = _a.label,
    _c = _a.onDark,
    onDark = _c === void 0 ? true : _c,
    _d = _a.disabled,
    disabled = _d === void 0 ? false : _d,
    rest = __rest(_a, ["children", "label", "onDark", "disabled"]);
  var wrenchModalCloseButton = useStyles$5({
    disabled: disabled,
    onDark: onDark
  }).wrenchModalCloseButton;
  return jsx("button", __assign({
    type: "button",
    className: wrenchModalCloseButton,
    "aria-label": label,
    disabled: disabled
  }, safeRest(rest), {
    children: jsx(IconX, {
      title: "close",
      color: onDark ? 'dark' : 'light'
    })
  }));
};

var useStyles$4 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchModalBody: {
      borderTop: function (_a) {
        var showHeaderShadow = _a.showHeaderShadow;
        return showHeaderShadow ? 'solid 1px #ddd' : null;
      },
      borderBottom: function (_a) {
        var showFooterShadow = _a.showFooterShadow;
        return showFooterShadow ? 'solid 1px #ddd' : null;
      },
      position: 'relative',
      overflowY: 'auto',
      flexGrow: 1,
      padding: function (_a) {
        var padding = _a.padding;
        return padding ? [[spacing[4], spacing[6]]] : undefined;
      }
    }
  };
});

var ModalBody = /*#__PURE__*/forwardRef(function (_a, ref) {
  _a.className;
    var children = _a.children,
    handleClick = _a.handleClick,
    id = _a.id,
    _b = _a.padding,
    padding = _b === void 0 ? true : _b,
    rest = __rest(_a, ["className", "children", "handleClick", "id", "padding"]);
  var containerRef = useRef(null);
  var aboveBodyRef = useRef(null);
  var belowBodyRef = useRef(null);
  var showHeaderShadow = useIntersectionObserver({
    containerRef: containerRef,
    observerOverlapRef: aboveBodyRef,
    disable: false
  });
  var showFooterShadow = useIntersectionObserver({
    containerRef: containerRef,
    observerOverlapRef: belowBodyRef,
    disable: false
  });
  var wrenchModalBody = useStyles$4({
    padding: padding,
    showHeaderShadow: showHeaderShadow,
    showFooterShadow: showFooterShadow
  }).wrenchModalBody;
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === KEYS.ESC) {
      handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
      e.preventDefault();
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsxs("section", __assign({
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      ref: containerRef,
      id: id,
      className: wrenchModalBody,
      onKeyDown: handleKeyDown
    }, safeRest(rest), {
      children: [jsx("div", {
        ref: aboveBodyRef
      }), children, jsx("div", {
        ref: belowBodyRef
      })]
    }))
  );
});
ModalBody.displayName = 'ModalBody';

var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  return {
    wrenchModalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: [[spacing[4], spacing[6], spacing[6]]],
      background: palette.background.light,
      color: palette.text.dark
    },
    wrenchModalActions: {
      display: 'flex',
      flexGrow: 1,
      flexWrap: 'wrap',
      justifyContent: function (_a) {
        var align = _a.align;
        return align === 'left' ? 'flex-start' : 'flex-end';
      },
      '&:not([class^="wrenchButtonGroup"])': {
        '& > button, & > a': {
          marginLeft: spacing[2]
        }
      }
    }
  };
});

var ModalFooter = function ModalFooter(_a) {
  var _b = _a.align,
    align = _b === void 0 ? 'left' : _b,
    children = _a.children;
  var _c = useStyles$3({
      align: align
    }),
    wrenchModalActions = _c.wrenchModalActions,
    wrenchModalFooter = _c.wrenchModalFooter;
  return jsx("footer", {
    className: wrenchModalFooter,
    children: jsx("div", {
      className: wrenchModalActions,
      children: children
    })
  });
};

var MODAL_SIZES = ['sm', 'md', 'lg', 'xl', 'full'];
var HERO_BACKGROUND_COLORS = ['dark', 'light'];
var HERO_SEMANTIC_COLORS = ['primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
var HERO_COLORS = __spreadArray(__spreadArray(__spreadArray([], HERO_BACKGROUND_COLORS, true), HERO_SEMANTIC_COLORS, true), ['transparent'], false);

var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  var heroColor = function heroColor(backgroundColor) {
    if (HERO_SEMANTIC_COLORS.includes(backgroundColor)) {
      return palette.background[backgroundColor].light;
    } else if (HERO_BACKGROUND_COLORS.includes(backgroundColor)) {
      return palette.background[backgroundColor];
    }
    return 'transparent';
  };
  return {
    wrenchModalHero: {
      backgroundColor: function (_a) {
        var backgroundColor = _a.backgroundColor;
        return heroColor(backgroundColor);
      },
      padding: function (_a) {
        var padding = _a.padding;
        return padding ? [[0, spacing[6], 0]] : null;
      },
      marginTop: function (_a) {
        var padding = _a.padding;
        return !padding ? -spacing[6] : null;
      },
      '& iframe': {
        aspectRatio: '16 / 9',
        height: 'auto',
        width: '100%'
      },
      '& > img': {
        width: '100%'
      }
    }
  };
});

var ModalHero = function ModalHero(_a) {
  var children = _a.children,
    _b = _a.backgroundColor,
    backgroundColor = _b === void 0 ? 'transparent' : _b,
    _c = _a.padding,
    padding = _c === void 0 ? true : _c;
  var wrenchModalHero = useStyles$2({
    backgroundColor: backgroundColor,
    padding: padding
  }).wrenchModalHero;
  return jsx("section", {
    className: wrenchModalHero,
    children: children
  });
};

var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize;
  return {
    wrenchModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: [0, spacing[6], 0, spacing[6]],
      marginTop: spacing[5],
      marginBottom: spacing[4],
      background: palette.background.light,
      flexShrink: 0,
      flexDirection: function (_a) {
        var stretched = _a.stretched;
        return stretched ? 'column' : 'row';
      }
    },
    wrenchModalText: {
      fontSize: 24,
      fontWeight: fontWeight.medium,
      color: palette.text.dark,
      margin: 0
    },
    wrenchModalTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchModalSubText: {
      fontSize: fontSize.bodySm,
      fontWeight: fontWeight.regular,
      color: palette.text.darkMuted,
      margin: [spacing[1], 0, 0]
    },
    wrenchModalAddOn: {
      flexGrow: 1,
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});

var ModalHeader = function ModalHeader(_a) {
  var addOn = _a.addOn,
    header = _a.header,
    id = _a.id,
    subHeader = _a.subHeader,
    _b = _a.stretched,
    stretched = _b === void 0 ? false : _b;
  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }
  var _c = useStyles$1({
      stretched: stretched
    }),
    wrenchModalAddOn = _c.wrenchModalAddOn,
    wrenchModalHeader = _c.wrenchModalHeader,
    wrenchModalSubText = _c.wrenchModalSubText,
    wrenchModalText = _c.wrenchModalText,
    wrenchModalTitle = _c.wrenchModalTitle;
  return jsxs("section", {
    className: wrenchModalHeader,
    children: [jsxs("div", {
      className: wrenchModalTitle,
      children: [header && jsx("h2", {
        className: wrenchModalText,
        id: id,
        children: header
      }), subHeader && jsx("p", {
        className: wrenchModalSubText,
        children: subHeader
      })]
    }), addOn && jsx("div", {
      className: wrenchModalAddOn,
      children: addOn
    })]
  });
};

var modalWidth = {
  sm: '443px',
  md: '676px',
  lg: '909px',
  xl: '1300px',
  full: '100%'
};
var useStyles = createMemoStyles(function (theme) {
  var _a;
  var breakpoints = theme.breakpoints,
    palette = theme.palette,
    shadow = theme.shadow,
    spacing = theme.spacing;
  return {
    wrenchModalWrapper: {
      overflow: 'auto',
      position: 'fixed',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      },
      '& > div': {
        width: function (_a) {
          var size = _a.size;
          return size === 'full' ? '100%' : null;
        },
        height: function (_a) {
          var size = _a.size;
          return size === 'full' ? '100%' : null;
        }
      }
    },
    wrenchModal: function (_a) {
      var size = _a.size;
      return {
        width: size === 'full' ? '100%' : null,
        height: size === 'full' ? '100%' : null
      };
    },
    wrenchModalContainer: __assign(__assign({}, defaultFonts(theme)), (_a = {
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      color: palette.text.dark,
      background: palette.text.light,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return (elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space) + 1;
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      whiteSpace: 'normal',
      outline: 'none',
      pointerEvents: 'all',
      paddingTop: spacing[6]
    }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      maxWidth: function (_a) {
        var size = _a.size;
        return {
          sm: '80vw',
          md: '80vw',
          lg: '80vw',
          xl: '96vw',
          full: '100vw'
        }[size];
      },
      maxHeight: function (_a) {
        var size = _a.size;
        return {
          sm: '90vh',
          md: '90vh',
          lg: '90vh',
          xl: '96vh',
          full: '100vh'
        }[size];
      },
      borderRadius: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? 0 : theme.radius.large;
      },
      boxShadow: shadow.space,
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      height: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? '100%' : 'auto';
      },
      width: function (_a) {
        var size = _a.size;
        return modalWidth[size];
      }
    }, _a)),
    wrenchModalOverlayContainer: function (_a) {
      var elevation = _a.elevation;
      return {
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space,
        position: 'relative'
      };
    },
    wrenchModalOverlay: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      position: 'fixed',
      backgroundColor: palette.background.overlay
    }
  };
});

__spreadArray(__spreadArray([], SIZES.filter(function (x) {
  return x !== 'xs';
}), true), ['full'], false);
var Modal = function Modal(_a) {
  var children = _a.children,
    _b = _a.closeButtonLabel,
    closeButtonLabel = _b === void 0 ? 'Close Modal' : _b,
    _c = _a.closeButtonOnDark,
    closeButtonOnDark = _c === void 0 ? true : _c,
    _d = _a.dismissible,
    dismissible = _d === void 0 ? true : _d,
    onClose = _a.onClose,
    _e = _a.show,
    show = _e === void 0 ? false : _e,
    _f = _a.size,
    size = _f === void 0 ? 'sm' : _f,
    title = _a.title,
    elevation = _a.elevation,
    _g = _a.role,
    role = _g === void 0 ? 'dialog' : _g,
    _h = _a.alwaysRenderContent,
    alwaysRenderContent = _h === void 0 ? false : _h,
    rest = __rest(_a, ["children", "closeButtonLabel", "closeButtonOnDark", "dismissible", "onClose", "show", "size", "title", "elevation", "role", "alwaysRenderContent"]);
  var _j = useState(show),
    showWrapper = _j[0],
    setShowWrapper = _j[1];
  useIsomorphicLayoutEffect(function () {
    if (isBrowser) {
      if (show) {
        document.body.style.overflowY = 'hidden';
      } else {
        document.body.style.overflowY = 'auto';
      }
    }
    return function () {
      if (isBrowser) {
        document.body.style.overflowY = 'auto';
      }
    };
  });
  var modalRef = /*#__PURE__*/createRef();
  var modalBodyRef = /*#__PURE__*/createRef();
  var prevShow = usePreviousState(show);
  var fullPage = size === 'full';
  var _k = useStyles({
      size: size,
      elevation: elevation,
      fullPage: fullPage,
      showWrapper: showWrapper
    }),
    wrenchModal = _k.wrenchModal,
    wrenchModalContainer = _k.wrenchModalContainer,
    wrenchModalOverlay = _k.wrenchModalOverlay,
    wrenchModalOverlayContainer = _k.wrenchModalOverlayContainer,
    wrenchModalWrapper = _k.wrenchModalWrapper;
  useEffect(function () {
    if (modalBodyRef.current && prevShow !== show) {
      modalBodyRef.current.focus({
        preventScroll: true
      });
    }
  }, [modalBodyRef, prevShow, show]);
  var titleId = useMemo(function () {
    return uniqueId('modal-title-');
  }, []);
  var bodyId = useMemo(function () {
    return uniqueId('modal-body-');
  }, []);
  var handleClick = function handleClick() {
    if (dismissible) {
      if (onClose) {
        onClose();
      }
    }
  };
  /**
   * ModalHeader exists show aria-labelledby
   * @type {boolean}
   */
  var hasModalHeader = Children.toArray(children).some(function (child) {
    return /*#__PURE__*/React.isValidElement(child) && child.type === ModalHeader;
  });
  var handleKeyDown = function handleKeyDown(event) {
    var _a;
    if (event.key === 'Tab' && modalRef.current) {
      var modalElements = (_a = modalRef === null || modalRef === void 0 ? void 0 : modalRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('*');
      var focusableElements = Array.from(modalElements).filter(function (element) {
        return isFocusableElement(element);
      });
      var firstElement = focusableElements[0];
      var lastElement = focusableElements[focusableElements.length - 1];
      if (isBrowser && focusableElements) {
        if (event.shiftKey && document.activeElement === firstElement) {
          lastElement === null || lastElement === void 0 ? void 0 : lastElement.focus();
          event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus();
          event.preventDefault();
        }
      }
    }
  };
  if (!showWrapper && show) {
    setShowWrapper(true);
  }
  return jsxs(Fragment, {
    children: [jsx("div", {
      className: fullPage ? '' : wrenchModalOverlayContainer,
      children: jsx(Fade, {
        in: show,
        children: jsx(Overlay, {
          onClick: handleClick,
          className: fullPage ? '' : wrenchModalOverlay
        })
      })
    }), jsx("div", {
      className: wrenchModalWrapper,
      children: jsx(Slide, {
        in: show,
        direction: "up",
        onEnter: function () {
          return setShowWrapper(true);
        },
        onExited: function () {
          return setShowWrapper(false);
        },
        alwaysRenderContent: alwaysRenderContent,
        children: jsx("div", __assign({
          className: wrenchModal
        }, safeRest(rest), {
          children: jsxs("div", {
            className: wrenchModalContainer,
            "aria-label": title,
            "aria-labelledby": hasModalHeader ? titleId : undefined,
            "aria-describedby": bodyId,
            role: role,
            "aria-modal": "true",
            tabIndex: -1,
            onKeyDown: handleKeyDown,
            ref: modalRef,
            children: [dismissible && jsx(CloseButton, {
              onClick: handleClick,
              label: closeButtonLabel,
              onDark: closeButtonOnDark
            }), Children.map(children, function (child) {
              if (/*#__PURE__*/React.isValidElement(child)) {
                var additionalProps = void 0;
                if (child.type === ModalHeader) {
                  additionalProps = {
                    id: titleId
                  };
                } else if (child.type === ModalBody) {
                  additionalProps = {
                    id: bodyId,
                    handleClick: handleClick,
                    ref: modalBodyRef
                  };
                }
                if (additionalProps) {
                  if (/*#__PURE__*/React.isValidElement(child)) {
                    return /*#__PURE__*/React.cloneElement(child, additionalProps);
                  }
                }
              }
              return child;
            })]
          })
        }))
      })
    })]
  });
};
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Hero = ModalHero;
Modal.Header = ModalHeader;

export { HERO_BACKGROUND_COLORS, HERO_COLORS, HERO_SEMANTIC_COLORS, MODAL_SIZES, Modal };
